import type { QueryClient } from '@tanstack/react-query';
import { ApiError, api, setQueuer, type QueuedCall } from '../api/client';

/**
 * The outbox: changes made while the connection is down are kept on the device and sent, in the order they were
 * made, when it comes back.
 *
 * Only actions that are safe to repeat and safe to send late are ever queued (the screens opt in one by one):
 * housekeeping progress, adding and sending order items, marking a room clean. Taking payment, refunding and
 * anything else that moves money is never queued: it needs a live answer from the server.
 *
 * Nothing is ever dropped silently. A change the server refuses (someone else already did it, the order was
 * cancelled) is kept in the list as "needs a look" with the server's reason until a person dismisses it.
 */
export interface QueuedRequest extends QueuedCall {
  id: string;
  staffId: string;
  createdAt: number;
  status: 'waiting' | 'failed';
  error?: string;
}

export interface OutboxStore {
  load(): Promise<QueuedRequest[]>;
  save(items: QueuedRequest[]): Promise<void>;
}

export class MemoryStore implements OutboxStore {
  items: QueuedRequest[] = [];
  async load(): Promise<QueuedRequest[]> {
    return structuredClone(this.items);
  }
  async save(items: QueuedRequest[]): Promise<void> {
    this.items = structuredClone(items);
  }
}

/** The whole list in one IndexedDB record. Storage that is blocked or full degrades to memory for this visit. */
export class IndexedDbStore implements OutboxStore {
  private db: Promise<IDBDatabase> | null = null;
  private open(): Promise<IDBDatabase> {
    this.db ??= new Promise((resolve, reject) => {
      const req = indexedDB.open('aa-hotel-outbox', 1);
      req.onupgradeneeded = () => req.result.createObjectStore('kv');
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.db;
  }
  async load(): Promise<QueuedRequest[]> {
    try {
      const db = await this.open();
      return await new Promise((resolve, reject) => {
        const r = db.transaction('kv').objectStore('kv').get('items');
        r.onsuccess = () => resolve((r.result as QueuedRequest[] | undefined) ?? []);
        r.onerror = () => reject(r.error);
      });
    } catch {
      return [];
    }
  }
  async save(items: QueuedRequest[]): Promise<void> {
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('kv', 'readwrite');
        tx.objectStore('kv').put(items, 'items');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      /* the changes stay in memory for this visit */
    }
  }
}

export interface OutboxState {
  online: boolean;
  flushing: boolean;
  /** this person's changes that have not been sent yet */
  waiting: QueuedRequest[];
  /** changes the server refused, kept until dismissed */
  failed: QueuedRequest[];
}

const PROBE_MS = 8_000;

export class Outbox {
  private items: QueuedRequest[] = [];
  private owner: string | null = null;
  private online = true;
  private flushing = false;
  private qc: QueryClient | null = null;
  private listeners = new Set<() => void>();
  private snapshot: OutboxState = { online: true, flushing: false, waiting: [], failed: [] };
  private timer: ReturnType<typeof setInterval> | null = null;
  /** told when a change is saved for later, so the screen can say so */
  onQueued: ((label: string) => void) | null = null;

  constructor(
    private readonly store: OutboxStore,
    private readonly probe: () => Promise<boolean> = defaultProbe,
  ) {}

  /* --------------------------------------------------------------- wiring */
  attach(qc: QueryClient): void {
    this.qc = qc;
  }

  start(): void {
    setQueuer((call) => this.enqueue(call));
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void this.flush());
      window.addEventListener('offline', () => this.setOnline(false));
      if (!navigator.onLine) this.online = false;
    }
    this.timer ??= setInterval(() => void this.tick(), PROBE_MS);
  }

  stop(): void {
    setQueuer(null);
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Who is signed in. Only their changes are ever sent; someone else's wait on the device until they return. */
  async setOwner(staffId: string | null): Promise<void> {
    this.owner = staffId;
    this.items = await this.store.load();
    this.publish();
    if (staffId) void this.flush();
  }

  /* ---------------------------------------------------------------- state */
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = (): OutboxState => this.snapshot;

  private publish(): void {
    const mine = this.items.filter((i) => i.staffId === this.owner);
    this.snapshot = { online: this.online, flushing: this.flushing, waiting: mine.filter((i) => i.status === 'waiting'), failed: mine.filter((i) => i.status === 'failed') };
    for (const l of this.listeners) l();
  }

  private async persist(): Promise<void> {
    await this.store.save(this.items);
  }

  private setOnline(v: boolean): void {
    if (this.online === v) return;
    this.online = v;
    this.publish();
  }

  /* -------------------------------------------------------------- queueing */
  /** Saves a change for later. Returns false when there is nobody signed in to own it, so the caller reports the failure instead. */
  enqueue(call: QueuedCall): boolean {
    if (!this.owner) return false;
    this.items.push({ ...call, id: crypto.randomUUID(), staffId: this.owner, createdAt: Date.now(), status: 'waiting' });
    this.online = false; // the request that brought us here could not reach the server
    this.publish();
    void this.persist();
    this.onQueued?.(call.label);
    return true;
  }

  dismiss(id: string): void {
    this.items = this.items.filter((i) => i.id !== id);
    this.publish();
    void this.persist();
  }

  retry(id: string): void {
    this.items = this.items.map((i) => (i.id === id ? { ...i, status: 'waiting' as const, error: undefined } : i));
    this.publish();
    void this.persist();
    void this.flush();
  }

  /* --------------------------------------------------------------- sending */
  private async tick(): Promise<void> {
    if (this.flushing) return;
    const hasWork = this.snapshot.waiting.length > 0;
    if (this.online && !hasWork) return;
    if (await this.probe()) {
      this.setOnline(true);
      await this.flush();
    } else {
      this.setOnline(false);
    }
  }

  /**
   * Sends this person's waiting changes, oldest first. A change the server refuses is marked "needs a look" and the
   * rest carry on; a connection or server problem stops the run so order is kept and nothing is lost.
   */
  async flush(): Promise<void> {
    if (this.flushing || !this.owner) return;
    this.flushing = true;
    this.publish();
    let sent = 0;
    try {
      for (const item of this.items.filter((i) => i.staffId === this.owner && i.status === 'waiting')) {
        try {
          await api(item.path, { method: item.method, ...(item.body !== undefined ? { body: item.body } : {}), idempotencyKey: item.idempotencyKey });
          this.items = this.items.filter((i) => i.id !== item.id);
          sent++;
          this.online = true;
        } catch (e) {
          if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 429) {
            this.items = this.items.map((i) => (i.id === item.id ? { ...i, status: 'failed' as const, error: e.message } : i));
            continue;
          }
          this.online = !(e instanceof ApiError && e.status === 0) && this.online; // unreachable: try again later
          break;
        } finally {
          await this.persist();
          this.publish();
        }
      }
    } finally {
      this.flushing = false;
      this.publish();
    }
    if (sent > 0) void this.qc?.invalidateQueries();
  }
}

async function defaultProbe(): Promise<boolean> {
  try {
    const r = await fetch('/api/health', { cache: 'no-store' });
    return r.ok;
  } catch {
    return false;
  }
}

/** The one outbox the app uses. */
export const outbox = new Outbox(new IndexedDbStore());
